import React, { useCallback, useEffect, useState } from 'react';
import { selectedAccountSelector } from '@core/store/library-stores';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { usePmZoneSiteListDataSource } from '@features/dataSource';
import { Fade, Typography } from '@material-ui/core';
import { ContentWrapper, CustomHeader } from '@shared/components';
import { setSiteIdURL } from '@shared/hooks/useSiteIdStateQuery';
import { PMZoneSiteModel } from '@shared/models';
import { t, Trans } from '@lingui/macro';
import { SiteListItem } from './SiteListItem';
import { NoPmZone } from '../NoPmZone';
import { useHistory } from 'react-router-dom';
import { AppPaths } from '@core/routes/routesPaths';
import { CustomSelectItem, Button, LoadingData, useNotifications, AsyncCustomSelect } from '@hwm/ui-lib';
import { applicationPaths } from '@core/http/applicationPaths';
import { httpClient } from '@core/http/httpClient';
import { MatchedAccOrSite } from '../Sider/models';
import { pmZoneSitesReducer } from '@core/store/entity/pmzoneSites/PMZoneSitesReducer';
import { usePermissions } from '@shared/hooks/usePermissions';
import { preferencesSelector } from '@core/store/entity/preferences/PreferencesSelectors';
import { accountsThunks } from '@core/store/library-stores/accounts.thunks';
import { isEmpty } from 'lodash';

export const ADD_REMOVE_SITE_DELAY = 2000;

interface SitesListProps {
  showHeader?: boolean;
  clickOnSite?: () => void;
  closeBtn?: {
    onClose?: () => void;
    btnName?: string;
  };
  minHeight?: number;
  showUnassignedSites?: boolean;
  showDescriptions?: boolean;
  /**
   * `dataTestId` - name of the Sites List (preferably `<name>-pmz-sites`)
   * @example
   * ```tsx
   * <SitesList dataTestId="test-pmz-sites" />
   */
  dataTestId: string;
}

export const SitesList: React.FC<SitesListProps> = ({ showHeader, clickOnSite, closeBtn, minHeight, dataTestId }) => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { canEditSites } = usePermissions();
  const { handleError } = useNotifications();

  const [loading, setLoading] = useState(false);
  const [siteToAdd, setSiteToAdd] = useState<CustomSelectItem>(null);

  const { preferences: units } = useAppSelector(preferencesSelector);
  const { id, name: accountName, isPmZone } = useAppSelector(selectedAccountSelector);
  const { pmZoneSites, getPmZoneSites, pmZoneSitesLoading } = usePmZoneSiteListDataSource();

  const siteClick = useCallback(
    (siteId: number) => {
      clickOnSite && clickOnSite();
      history.push(AppPaths.alarmsView.path.base);
      setSiteIdURL(siteId.toString());
    },
    [clickOnSite, history]
  );

  const addNewSiteToPMZone = useCallback(() => {
    if (!siteToAdd || !id) return;

    dispatch(
      pmZoneSitesReducer.thunks.addSiteToPMZone({
        accountId: id,
        siteId: siteToAdd.value as number,
      })
    )
      .then(() => {
        setLoading(true);

        setTimeout(() => {
          getPmZoneSites({ accountId: id?.toString() })
            .then(() => {
              setSiteToAdd(null);
            })
            .finally(() => {
              setLoading(false);
            });
          dispatch(accountsThunks.getPmZoneData({ accountId: id, units }));
        }, ADD_REMOVE_SITE_DELAY);
      })
      .catch(() => {
        handleError(t`There was an error while trying to add new site to PMZone`, t`PMZone`);
      });
  }, [siteToAdd, id, units, dispatch, handleError, getPmZoneSites]);

  const displaySites = useCallback(
    (pmZoneSites?: PMZoneSiteModel[]) => {
      const prvSite = pmZoneSites?.find((s) => s.siteType === 'PRV' && s.isAssignedToPmZone) || null;
      const sitesToDisplay = pmZoneSites
        ?.filter((s) => s.id !== prvSite?.id)
        ?.sort((a, b) => (a.siteType === 'PRV' ? -1 : a.siteType === 'CP' && b.siteType === 'Unknown' ? -1 : 0));

      return (
        <div className="flex flex-col flex-wrap" data-testid={`${dataTestId}`}>
          {Boolean(prvSite?.id) ? (
            <SiteListItem
              site={prvSite}
              click={() => siteClick(prvSite.id)}
              canRemove={canEditSites}
              setLoading={setLoading}
              dataTestId={`${dataTestId}-site-0`}
            />
          ) : null}
          {sitesToDisplay?.map((site, i) => (
            <SiteListItem
              key={site.id}
              site={site}
              click={() => siteClick(site.id)}
              canRemove={canEditSites}
              setLoading={setLoading}
              dataTestId={`${dataTestId}-site-${Boolean(prvSite?.id) ? i + 1 : i}`}
            />
          )) ?? null}
        </div>
      );
    },
    [canEditSites, dataTestId, siteClick, setLoading]
  );

  const closeButtonComponent = useCallback(
    (closeFn: () => void) => (
      <Button variant="outlined" onClick={closeFn} dataTestId={`${dataTestId}-header-buttons-close-button`}>
        {closeBtn?.btnName ?? (
          <Trans id="[Action.Close]" comment="Close as verb">
            Close
          </Trans>
        )}
      </Button>
    ),
    [dataTestId, closeBtn?.btnName]
  );

  useEffect(() => {
    if (id && isPmZone) {
      setLoading(true);
      getPmZoneSites({ accountId: id?.toString() }).finally(() => setLoading(false));
    }
  }, [id, isPmZone, setLoading, getPmZoneSites]);

  return (
    <div
      className="flex flex-col flex-start h-full w-full overflow-hidden mt-2"
      style={{ minHeight: minHeight ?? 300 }}
      data-testid={`${dataTestId}-wrapper`}
    >
      {showHeader && (
        <CustomHeader
          title={`${accountName} ${t`Site List`}`}
          buttons={closeBtn?.onClose ? [closeButtonComponent(closeBtn.onClose)] : undefined}
          dataTestId={`${dataTestId}-header`}
        />
      )}
      {!pmZoneSitesLoading && !loading && canEditSites && (
        <ContentWrapper
          title={t({
            message: 'Add site',
            id: '[PMZDetailsSection.AddSite]',
          })}
          collapsed
          dataTestId={`${dataTestId}-add-site-content-wrapper`}
        >
          <div className="flex flex-col items-end my-3 w-full space-y-2">
            <div className="w-full">
              <AsyncCustomSelect
                placeholder={t`Search by Address, ID, Serial or SMS`}
                onChange={(value) => setSiteToAdd(value as CustomSelectItem)}
                onLoadData={async (value: string) => {
                  const prvUrl = applicationPaths.sites.getPrvAutocomplete;
                  const cpUrl = applicationPaths.sites.getCpAutocomplete;

                  return Promise.all([
                    await httpClient.get<MatchedAccOrSite[]>(prvUrl, {
                      params: { name: value },
                    }),
                    await httpClient.get<MatchedAccOrSite[]>(cpUrl, {
                      params: { name: value },
                    }),
                  ]).then((data) => {
                    const options = data?.flatMap((sitesData, i) =>
                      sitesData?.data
                        ?.filter((site) => !Boolean(pmZoneSites?.find((s) => s?.id === site?.id)))
                        ?.map((site) => {
                          return {
                            label: `[${
                              i === 0
                                ? t({ message: 'PRV', id: '[SiteType.PRV]' })
                                : t({ message: 'CP', id: '[SiteType.CP]' })
                            }] ${site.name}`,
                            value: site.id,
                          } as CustomSelectItem;
                        })
                    );
                    return options;
                  });
                }}
                value={siteToAdd || null}
                isReadonly={!canEditSites}
                dataTestId={`${dataTestId}-add-site-async-select`}
              />
            </div>

            <div className="flex flex-row">
              <Button
                style={{ marginLeft: 8 }}
                onClick={() => addNewSiteToPMZone()}
                disabled={!Boolean(siteToAdd)}
                dataTestId={`${dataTestId}-add-site-button`}
              >
                <Trans>Add</Trans>
              </Button>
              <Button
                variant="outlined"
                onClick={() => setSiteToAdd(null)}
                style={{ marginLeft: 8 }}
                disabled={!Boolean(siteToAdd)}
                dataTestId={`${dataTestId}-clear-site-button`}
              >
                <Trans>Clear</Trans>
              </Button>
            </div>
          </div>
        </ContentWrapper>
      )}

      <div className="flex flex-col h-full overflow-y-auto mb-2">
        {isPmZone ? (
          pmZoneSitesLoading || loading ? (
            <LoadingData dataTestId={`${dataTestId}-loading`} />
          ) : (
            <Fade in={!pmZoneSitesLoading} {...{ timeout: 800 }}>
              <div className="mr-2 h-full">
                {pmZoneSites && !isEmpty(pmZoneSites) ? (
                  <>{displaySites(pmZoneSites)}</>
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <Typography variant="h6" data-testid={`${dataTestId}-no-sites`}>
                      <Trans>No sites assigned to PMZone</Trans>
                    </Typography>
                  </div>
                )}
              </div>
            </Fade>
          )
        ) : (
          <NoPmZone dataTestId={`${dataTestId}-no-data`} />
        )}
      </div>
    </div>
  );
};
